import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { PageLayout, Container } from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { Button, Input, Select, PageLoader, EmptyState } from '../components/UI';
import api from '../utils/api';

const Layout = styled.div`
  display: grid; grid-template-columns: 260px 1fr; gap: 28px;
  @media(max-width:1024px) { grid-template-columns: 1fr; }
`;
const Sidebar = styled.aside`
  background: #fff; border-radius: var(--radius-lg); padding: 20px;
  border: 1px solid var(--gray-100); height: fit-content;
  position: sticky; top: calc(var(--header-height) + 20px);
  @media(max-width:1024px) {
    display: ${p => p.$open ? 'block' : 'none'};
    position: fixed; top: 0; bottom: 0; left: 0; z-index: 200;
    width: 300px; border-radius: 0; overflow-y: auto;
    box-shadow: var(--shadow-xl);
  }
`;
const SidebarOverlay = styled.div`
  display: none;
  @media(max-width:1024px) { display: ${p => p.$open ? 'block' : 'none'}; position: fixed; inset: 0; background: rgba(0,0,0,.3); z-index: 199; }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 18px;
  @media(max-width:480px) { grid-template-columns: repeat(2, 1fr); gap: 10px; }
`;
const FilterSection = styled.div` border-top: 1px solid var(--gray-100); padding: 16px 0; &:first-child { border-top: none; padding-top: 0; } `;
const FilterTitle = styled.div` font-size: .88rem; font-weight: 600; color: var(--gray-800); margin-bottom: 12px; `;
const FilterOpt = styled.label` display: flex; align-items: center; gap: 8px; font-size: .88rem; color: var(--gray-600); cursor: pointer; padding: 3px 0; input { accent-color: var(--primary); } `;
const PageBtn = styled.button`
  width: 36px; height: 36px; border-radius: var(--radius-sm);
  border: 1.5px solid ${p => p.$active ? 'var(--primary)' : 'var(--gray-200)'};
  background: ${p => p.$active ? 'var(--primary)' : '#fff'};
  color: ${p => p.$active ? '#fff' : 'var(--gray-700)'};
  cursor: pointer; font-size: .88rem; transition: var(--transition);
  &:disabled { opacity: .4; cursor: not-allowed; }
  &:hover:not(:disabled) { border-color: var(--primary); }
`;

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filters state
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sort, setSort] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { api.get('/categories').then(d => setCategories(d.categories || [])).catch(() => {}); }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ page, limit: 12 });
      if (category) q.set('category', category);
      if (search)   q.set('search', search);
      if (sort)     q.set('sort', sort);
      if (minPrice) q.set('min_price', minPrice);
      if (maxPrice) q.set('max_price', maxPrice);
      if (searchParams.get('featured')) q.set('featured', '1');
      const d = await api.get(`/products?${q}`);
      setProducts(d.products || []);
      setTotal(d.total || 0);
      setPages(d.pages || 1);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [page, category, search, sort, minPrice, maxPrice, searchParams]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(loadProducts, 500);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const clearFilters = () => { setSearch(''); setCategory(''); setMinPrice(''); setMaxPrice(''); setSort(''); setPage(1); };

  const pageNums = Array.from({ length: pages }, (_, i) => i + 1).filter(n => n === 1 || n === pages || Math.abs(n - page) <= 2);

  return (
    <PageLayout>
      {/* Page header */}
      <div style={{ background:'#fff', borderBottom:'1px solid var(--gray-100)', padding:'20px 0' }}>
        <Container>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
            <div>
              <h1 style={{ fontSize:'1.6rem', marginBottom:4 }}>
                {category ? categories.find(c=>c.slug===category)?.name || 'Shop' : 'All Products'}
              </h1>
              <div style={{ fontSize:'0.85rem', color:'var(--gray-500)' }}>{total} products found</div>
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'center' }}>
              <Button $variant="outline" $size="sm" onClick={() => setSidebarOpen(p=>!p)} style={{ display:'none' }} className="filter-toggle-btn">
                ⚙ Filters
              </Button>
              <Select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }} style={{ width:'auto', fontSize:'0.88rem' }}>
                <option value="">Sort: Newest</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="rating">Top Rated</option>
              </Select>
            </div>
          </div>
        </Container>
      </div>

      <Container style={{ padding:'28px 20px' }}>
        <SidebarOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />
        <Layout>
          {/* Sidebar */}
          <Sidebar $open={sidebarOpen}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ fontSize:'1rem', margin:0 }}>Filters</h3>
              <button onClick={clearFilters} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.82rem', color:'var(--gray-400)' }}>Clear all</button>
            </div>

            <FilterSection>
              <FilterTitle>Search</FilterTitle>
              <Input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
            </FilterSection>

            <FilterSection>
              <FilterTitle>Categories</FilterTitle>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <FilterOpt><input type="radio" name="cat" value="" checked={!category} onChange={() => { setCategory(''); setPage(1); }} /> All Categories</FilterOpt>
                {categories.map(c => (
                  <FilterOpt key={c.id}>
                    <input type="radio" name="cat" value={c.slug} checked={category === c.slug} onChange={() => { setCategory(c.slug); setPage(1); }} />
                    {c.name}
                  </FilterOpt>
                ))}
              </div>
            </FilterSection>

            <FilterSection>
              <FilterTitle>Price Range</FilterTitle>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <Input placeholder="Min" type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)} style={{ width:80 }} />
                <span style={{ color:'var(--gray-400)' }}>—</span>
                <Input placeholder="Max" type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} style={{ width:80 }} />
              </div>
            </FilterSection>

            <FilterSection>
              <Button $block $size="sm" onClick={() => { loadProducts(); setSidebarOpen(false); }}>Apply Filters</Button>
            </FilterSection>
          </Sidebar>

          {/* Products */}
          <div>
            {loading ? <PageLoader /> : !products.length
              ? <EmptyState icon="🔍" title="No products found" subtitle="Try adjusting your filters" action={<Button $variant="outline" onClick={clearFilters}>Clear Filters</Button>} />
              : <Grid>{products.map(p => <ProductCard key={p.id} product={p} />)}</Grid>}

            {/* Pagination */}
            {pages > 1 && (
              <div style={{ display:'flex', gap:6, justifyContent:'center', marginTop:32, flexWrap:'wrap' }}>
                <PageBtn onClick={() => setPage(p=>p-1)} disabled={page===1}>‹</PageBtn>
                {pageNums.map((n, i) => {
                  const prev = pageNums[i-1];
                  return (
                    <React.Fragment key={n}>
                      {prev && n - prev > 1 && <span style={{ padding:'0 4px', color:'var(--gray-400)' }}>…</span>}
                      <PageBtn $active={page===n} onClick={() => setPage(n)}>{n}</PageBtn>
                    </React.Fragment>
                  );
                })}
                <PageBtn onClick={() => setPage(p=>p+1)} disabled={page===pages}>›</PageBtn>
              </div>
            )}
          </div>
        </Layout>
      </Container>
      <style>{`@media(max-width:1024px){.filter-toggle-btn{display:flex!important}}`}</style>
    </PageLayout>
  );
}
